import * as React from "react";
import helpConfig, { faqUrlFor } from "/src/helpers/helpConfig";

// Help section (prototype chrome-itinerary.jsx HelpfulInfo): one white card holding up to FOUR
// accent-coloured tiles — Get in Touch, WhatsApp Support, FAQs, Booked a Transfer? —
// each with a 52px coloured icon tile and a full-width soft-fill CTA block that opens the website
// in a new tab. The transfer tile is only shown when the booking actually includes a transfer;
// the FAQ tile links to the brand's FAQ page (Island Hopping has its own).
// WhatsApp Support is switched off for now (helpConfig.whatsAppEnabled) — the tile stays here so
// turning it back on is a one-line change.
// TODO: swap the inline WhatsApp glyph for the official brand asset when the business supplies it.

const strokeIcon = (paths, size = 25) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {paths}
    </svg>
);

const buildCards = (productType) => [
    {
        key: "contact",
        title: "Get in Touch",
        body: "Find the right number for help before your holiday starts.",
        cta: "Contact us",
        accent: "#2e2266", soft: "#ece9fb",
        href: helpConfig.contactUrl,
        icon: strokeIcon(<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />),
    },
    {
        key: "whatsapp",
        title: "WhatsApp\nSupport",
        body: "Chat with our team on WhatsApp for quick assistance.",
        cta: "Open WhatsApp",
        accent: "#1faa53", soft: "#e8f7ed",
        href: `https://wa.me/${helpConfig.whatsAppNumber}`,
        icon: (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
            </svg>
        ),
    },
    {
        key: "faq",
        title: "Frequently Asked Questions",
        body: "Find answers to the most common questions.",
        cta: "View FAQs",
        accent: "#ee7d1e", soft: "#fdead3", ctaText: "#b5570a",
        href: faqUrlFor(productType),
        icon: strokeIcon(<><path d="M9.1 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /><circle cx="12" cy="12" r="10" /></>),
    },
    {
        key: "transfer",
        title: "Booked a Transfer?",
        body: "Help and info for your transfer on the day.",
        cta: "View transfer info",
        accent: "#10786f", soft: "#d9efeb",
        href: helpConfig.transferInfoUrl,
        icon: strokeIcon(<><rect x="1" y="7" width="15" height="10" rx="2" /><path d="M16 10h3.2a2 2 0 0 1 1.7.95L22.4 13.4a2 2 0 0 1 .3 1.05V16a1 1 0 0 1-1 1h-2.2" /><circle cx="6" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></>, 27),
    },
];

export default function HelpSection({ showTransfers = true, productType = null }) {
    const cards = buildCards(productType).filter((c) =>
        (c.key !== "transfer" || showTransfers) && (c.key !== "whatsapp" || helpConfig.whatsAppEnabled));
    // One column per tile, so a dropped tile widens the rest rather than leaving a gap.
    const gridCols = cards.length >= 4 ? "lg:grid-cols-4" : cards.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2";
    return (
        <section className="bg-white rounded-card shadow-card px-4 md:px-8 pt-7 pb-8 mb-6">
            <div className="flex items-center gap-4 mb-[22px]">
                <div className="w-[46px] h-[46px] rounded-full border-2 border-primary flex items-center justify-center shrink-0">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-primary" aria-hidden="true">
                        <path d="M11 10h2v7h-2v-7zm0-4h2v2h-2V6z" />
                    </svg>
                </div>
                <h2 className="text-[22px] md:text-[26px] font-poppins font-bold text-primary">Helpful information for your holiday</h2>
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4`}>
                {cards.map((c) => (
                    <div key={c.cta} className="border border-content_5 rounded-[14px] px-5 pt-5 pb-[18px] flex flex-col">
                        <div className="w-[52px] h-[52px] rounded-[13px] flex items-center justify-center mb-4" style={{ background: c.accent }}>
                            {c.icon}
                        </div>
                        <h3 className="text-[19px] font-poppins font-bold text-primary leading-tight whitespace-pre-line">{c.title}</h3>
                        <p className="mt-2.5 mb-[18px] text-sm text-content_2 leading-normal flex-1">{c.body}</p>
                        {/* Every one of these leaves My Booking for the website, so they open in a
                            new tab — the customer keeps their booking behind them — and carry the
                            open-in-new-window glyph rather than the usual chevron. */}
                        <a href={c.href} target="_blank" rel="noopener noreferrer"
                           className="flex items-center justify-between gap-2 px-4 py-[11px] rounded-[10px] font-bold text-sm"
                           style={{ background: c.soft, color: c.ctaText || c.accent }}>
                            <span>{c.cta}<span className="sr-only"> (opens in a new window)</span></span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M14 3h7v7" /><path d="M21 3l-9 9" />
                                <path d="M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
                            </svg>
                        </a>
                    </div>
                ))}
            </div>
        </section>
    );
}
